import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Divider,
  Container,
  Grid,
  Paper,
  Link,
  Alert,
  CircularProgress
} from '@mui/material';
import { useI18n } from '../i18n';
import authService from '../services/authService';
import '../styles/login.css';

interface LoginFormProps {
  onLogin?: (email: string) => void;
  onError?: (error: string) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onLogin, onError }) => {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1920);

  // Detectar cambios en el ancho de la ventana
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determinar si es móvil (ancho < 1000px)
  const isMobile = windowWidth < 1000;

  const authenticateEmail = async (emailToAuth: string, firstNameToAuth?: string, lastNameToAuth?: string) => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');

      // Validar que el email esté presente
      console.log('🔍 Valores de autenticación:');
      console.log('📧 Email:', emailToAuth);
      
      if (!emailToAuth) {
        throw new Error('Email es requerido');
      }

      // Validación de nombre y apellido ya no es necesaria ya que no se permiten usuarios nuevos

      // Paso 1: Solicitar OTP
      setSuccess('Paso 1/2: Solicitando código de verificación...');
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Paso 2: Procesando solicitud
      setSuccess('Paso 2/2: Procesando solicitud...');
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Ejecutar solicitud de OTP
      const result = await authService.requestOTP(emailToAuth, firstNameToAuth, lastNameToAuth);

      if (result.success) {
        setSuccess('¡Autenticación exitosa! Redirigiendo a la verificación OTP...');
        
        // Esperar 2 segundos antes de proceder con el login
        setTimeout(() => {
          if (onLogin) {
            onLogin(emailToAuth);
          }
        }, 2000);
      } else {
        // Verificar si el error es sobre nombre y apellido requeridos (usuario no registrado)
        if (result.error && result.error.toLowerCase().includes('first name and last name')) {
          setSuccess(''); // Limpiar mensaje de éxito
          setError('User not registered. Please sign up in the Noah\'s Ark app first.');
          setIsLoading(false); // Detener la carga
        } else {
          setSuccess(''); // Limpiar mensaje de éxito
          setError(result.error);
          setIsLoading(false); // Detener la carga en caso de otros errores también
        }
        if (onError) {
          onError(result.error);
        }
      }
    } catch (error) {
      const errorMessage = 'Error de conexión. Por favor, intenta nuevamente.';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const authenticatePhone = async (phoneToAuth: string) => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');

      // Validar que el teléfono esté presente
      console.log('🔍 Valores de autenticación por teléfono:');
      console.log('📱 Teléfono:', phoneToAuth);
      
      if (!phoneToAuth) {
        throw new Error('Número de teléfono es requerido');
      }

      // Paso 1: Solicitar OTP
      setSuccess('Paso 1/2: Solicitando código de verificación...');
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Paso 2: Procesando solicitud
      setSuccess('Paso 2/2: Procesando solicitud...');
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Ejecutar solicitud de OTP por teléfono
      const result = await authService.requestPhoneOTP(phoneToAuth);

      if (result.success) {
        setSuccess('¡Autenticación exitosa! Redirigiendo a la verificación OTP...');
        
        // Esperar 2 segundos antes de proceder con el login
        setTimeout(() => {
          if (onLogin) {
            onLogin(phoneToAuth);
          }
        }, 2000);
      } else {
        setSuccess('');
        setError(result.error);
        setIsLoading(false);
        if (onError) {
          onError(result.error);
        }
      }
    } catch (error: any) {
      const errorMessage = 'Error de conexión. Por favor, intenta nuevamente.';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (loginMethod === 'email') {
      authenticateEmail(email);
    } else {
      authenticatePhone(phoneNumber);
    }
  };


  return (
    <Container maxWidth="lg" className="login-container" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', py: 4 }}>
      <Paper
        elevation={8}
        className="login-card"
        sx={{
          width: '100%',
          maxWidth: isMobile ? '100%' : 1000,
          borderRadius: 3,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          minHeight: isMobile ? 'auto' : 600,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
        }}
      >
        {/* Panel Izquierdo - Formulario de Login */}
        <Box
          className="login-form"
          sx={{
            flex: 1,
            backgroundColor: 'white',
            p: isMobile ? 3 : 6,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: isMobile ? '100%' : 'auto',
            minWidth: isMobile ? '100%' : 'auto',
            order: isMobile ? 2 : 0
          }}
        >
          {/* Logo y Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box className="logo-container" aria-hidden="true">
              <Box
                component="svg"
                viewBox="0 0 64 64"
                sx={{ width: 38, height: 38, color: 'white' }}
              >
                <path
                  fill="currentColor"
                  d="M17.6 29.7c4.2-2.6 8.7-3.9 14.4-3.9s10.2 1.3 14.4 3.9c4.7 2.9 7.2 7.4 6.3 11.4-1.1 5.1-6.5 7.4-12 5.2-3.2-1.3-5.5-2-8.7-2s-5.5.7-8.7 2c-5.5 2.2-10.9-.1-12-5.2-.9-4 1.6-8.5 6.3-11.4Zm-5-2.5c-3.5 0-6.3-3.5-6.3-7.8s2.8-7.8 6.3-7.8 6.3 3.5 6.3 7.8-2.8 7.8-6.3 7.8Zm12.2-5.9c-3.5 0-6.3-3.7-6.3-8.2s2.8-8.2 6.3-8.2 6.3 3.7 6.3 8.2-2.8 8.2-6.3 8.2Zm14.4 0c-3.5 0-6.3-3.7-6.3-8.2s2.8-8.2 6.3-8.2 6.3 3.7 6.3 8.2-2.8 8.2-6.3 8.2Zm12.2 5.9c-3.5 0-6.3-3.5-6.3-7.8s2.8-7.8 6.3-7.8 6.3 3.5 6.3 7.8-2.8 7.8-6.3 7.8Z"
                />
              </Box>
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 'bold',
                color: '#0E0F12',
                mb: 1,
                fontSize: '28px'
              }}
            >
              Noah's Ark Pet Connect
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#6B6F7B',
                fontSize: '14px',
                mb: 2
              }}
            >
              Pet Contest Ecosystem
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                color: '#0E0F12',
                fontSize: '24px'
              }}
            >
              One ark. One feeling.
            </Typography>
          </Box>

          {/* Mostrar error si existe */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {/* Mostrar mensaje de éxito si existe */}
          {success && (
            <Alert 
              severity="success" 
              sx={{ mb: 3, borderRadius: 2 }}
              icon={<CircularProgress size={20} />}
            >
              {success}
            </Alert>
          )}

          {/* Formulario de Login */}
          <Box component="form" onSubmit={handleSubmit} sx={{ mb: 4 }}>
            {loginMethod === 'email' ? (
              <>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                  disabled={isLoading || success !== ''}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Link
                    component="button"
                    type="button"
                    onClick={() => {
                      setLoginMethod('phone');
                      setEmail('');
                      setError('');
                    }}
                    sx={{
                      cursor: 'pointer',
                      textDecoration: 'none',
                      color: '#306FC8',
                      fontSize: '14px',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Login with phone number
                  </Link>
                </Box>
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Phone Number"
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="input-field"
                  disabled={isLoading || success !== ''}
                  placeholder="+1234567890"
                  sx={{ mb: 2 }}
                />
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Link
                    component="button"
                    type="button"
                    onClick={() => {
                      setLoginMethod('email');
                      setPhoneNumber('');
                      setError('');
                    }}
                    sx={{
                      cursor: 'pointer',
                      textDecoration: 'none',
                      color: '#306FC8',
                      fontSize: '14px',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Login with email
                  </Link>
                </Box>
              </>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              className="signin-button"
              disabled={isLoading || success !== ''}
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isLoading ? 'AUTHENTICATING...' : success ? 'REDIRECTING...' : 'SIGN IN'}
            </Button>
          </Box>

          {/* Texto informativo */}
          <Typography
            variant="body2"
            sx={{
              textAlign: 'center',
              color: '#6B6F7B',
              fontSize: '14px',
              mb: 3,
              mt: 2
            }}
          >
            Use the same user as in Noah's Ark app.
          </Typography>

          {/* Enlaces del Footer */}
          <Box className="footer-links">
            <Link href="#" className="footer-link">
              Privacy policy
            </Link>
            <Link href="#" className="footer-link">
              Terms & conditions
            </Link>
          </Box>
        </Box>

        {/* Panel Derecho - Imagen Decorativa */}
        <Box 
          className="login-image" 
          sx={{ 
            flex: 1,
            minHeight: isMobile ? '400px' : 'auto',
            display: isMobile ? 'block' : 'flex',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: isMobile ? 'flex-start' : 'center',
            order: isMobile ? 1 : 0
          }}
        >
          {/* Contenido decorativo */}
          <Box className="login-image-content">
            <Typography
              variant={isMobile ? "h4" : "h3"}
              sx={{
                fontWeight: 'bold',
                mb: 2,
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
              }}
            >
              Every contest feels like home.
            </Typography>
            <Typography
              variant={isMobile ? "body1" : "h6"}
              sx={{
                opacity: 0.9,
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
              }}
            >
              Warm, simple access for owners, partners, sponsors, and the pets they support.
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginForm;
